/**
 * A small, safe markdown renderer.
 *
 * Everything is HTML-escaped before any markup is generated, and only a fixed
 * set of tags is ever produced — so user content cannot inject scripts, and we
 * avoid shipping a parser plus a sanitiser for what pages and comments need.
 *
 * It is not CommonMark and does not try to be. Two omissions are deliberate
 * rather than unfinished. Raw HTML never survives, which is the whole point of
 * escaping first. And four-space indented code is not a code block, because
 * `toggleTask` counts checkboxes over the same source and cannot tell indented
 * code from a nested list without becoming a second parser — fences do that
 * job, and the editor's toolbar writes fences.
 */

import { escapeHtml, safeUrl, unescapeHtml } from './escape.ts';
import { htmlText, sanitizeHtml } from './html.ts';
import { headingAt, slugCounter, splitTarget, withoutCode } from './links.ts';

/**
 * What this workspace's work is called, so a reference can be recognised.
 *
 * Deliberately *not* a pattern like `[A-Z]+-\d+` on its own: that also matches
 * `UTF-8`, `COVID-19` and `ISO-8601`, and a chat line about a standard should
 * not sprout a broken link to a task nobody has. The caller knows which project
 * keys exist, so it says; with no keys given nothing is linked, which is what
 * the server wants when it renders a shared page for a reader who has no
 * workspace to link into.
 */
export interface MarkdownOptions {
  /** Project keys, as written in an identifier: `['WEB', 'APP']`. */
  keys?: readonly string[];
  /** Where a project lives, by key. Without this `#WEB` stays plain text. */
  projectHref?: (key: string) => string | undefined;
  /**
   * Whether a checkbox can be ticked where it is rendered.
   *
   * Off by default, and that is the safe way round: an enabled checkbox with
   * nothing listening toggles on screen and then silently disagrees with the
   * text it came from. Switched on, each box carries its index — counted top to
   * bottom, skipping fenced code — and `toggleTask` in `editor.ts` counts the
   * same way, which is what keeps a click and a line of markdown pointing at
   * each other.
   */
  interactiveTasks?: boolean;
  /**
   * Where `[[Onboarding]]` points, and whether that page exists yet.
   *
   * The same shape of contract as `keys` above and for the same reason: the
   * renderer cannot know which pages a reader may see, so it asks. Answering
   * `undefined` leaves the brackets as the author typed them, which is what the
   * server wants when it renders a shared page for somebody with no workspace
   * to link into — a link nobody can follow is worse than visible syntax.
   *
   * `missing` is a target no page answers to. Drawn differently rather than
   * left as text, because a wiki is written by linking to pages before they
   * exist: the broken link *is* the invitation to write one.
   */
  pageHref?: (target: string, heading: string | null) => { href: string; missing?: boolean } | undefined;
  /**
   * The text behind `![[Onboarding]]`, for a caller that wants it embedded.
   *
   * Transclusion asks a question `pageHref` does not: not "where is it" but
   * "what does it say", and only the caller knows which pages this reader may
   * be shown. Left out, `![[…]]` stays as the author typed it — the same rule
   * every other resolution here follows.
   *
   * The rendering recurses, so a page embedding itself, or two embedding each
   * other, would not stop. It is stopped exactly rather than by a depth
   * counter: a page already open on the path is drawn as a link instead, which
   * says what happened where a truncation would only look like a bug.
   */
  pageBody?: (target: string) => {
    id: string; title: string; href: string; content: string;
    /**
     * What the embedded page is written in.
     *
     * Without it every embed was read as markdown, so `![[Support hours]]`
     * naming an HTML page drew a wall of escaped tags where the table should
     * have been. An embed is a *view* of a page and has to look like the page.
     */
    format?: string;
  } | undefined;
  /**
   * What to say where an embed would repeat a page already open above it.
   *
   * A word, from the caller, because this file has no language: it renders the
   * same markup for three locales and a shared page nobody has signed into.
   * Left out, the link stands on its own — which is the honest minimum, since
   * the page it names *is* somewhere above.
   */
  embedLoopNote?: string;
  /**
   * Given, every heading carries `id="<prefix><slug>"` — so a section can be
   * linked to, from `[[Page#Section]]` or from an outline beside the text.
   *
   * A prefix rather than a flag because one document may hold several pages: a
   * shared page brings its children, and two of them are quite likely to have a
   * heading called Notes. Left out, headings carry no id at all, which is right
   * for a chat message — a hundred of those in a scroller would be a hundred
   * ids competing for the same fragment.
   */
  headingPrefix?: string;
}


const KEY_SHAPE = /^[A-Z][A-Z0-9]{0,9}$/;

/**
 * `[[Title]]` and `[[Title|what to call it]]`, as a link to that page.
 *
 * The finished anchor is stashed the way `references` stashes one, and for a
 * sharper reason than nesting: a page may be called `Q1_targets` or `#done`,
 * and the emphasis and tag rules further down would happily eat a title they
 * find in the open. Held as a placeholder, the label comes out as the page is
 * named.
 *
 * The target arrives HTML-escaped, because everything does — so it is turned
 * back into the five characters `escapeHtml` produced before it is looked up.
 * Skipping that made `[[Tools & toys]]` a link to a page nobody has.
 */
function pageLinks(html: string, refs: MarkdownOptions, stash: string[]): string {
  return html.replace(/\[\[([^[\]|\n]*)(?:\|([^[\]\n]*))?\]\]/g, (match, raw: string, alias: string | undefined) => {
    const { target, heading } = splitTarget(unescapeHtml(raw));
    if (!target && !heading) return match;
    const found = refs.pageHref!(target, heading);
    if (!found) return match;
    // What it is called, when the author did not say: the section if the link
    // named one, otherwise the page. `[[Onboarding#Day one]]` reads as its
    // section, because that is the part of the sentence doing the work.
    const label = (alias ?? '').trim() || escapeHtml(heading || target);
    stash.push(
      `<a class="md-page${found.missing ? ' md-page-new' : ''}" href="${escapeHtml(found.href)}">${label}</a>`,
    );
    return `${stash.length - 1}`;
  });
}

/**
 * Turn `WEB-42` into a link to that task, and `#WEB` into a link to that
 * project.
 *
 * Anchors that already exist are stashed first. Without that, a URL ending in
 * something identifier-shaped would grow a second anchor inside the first, and
 * a nested `<a>` is a link that swallows the one around it.
 */
function references(html: string, refs: MarkdownOptions): string {
  const keys = (refs.keys ?? []).filter((key) => KEY_SHAPE.test(key));
  if (!keys.length) return html;
  const known = new Set(keys);

  const links: string[] = [];
  // Anchors whole, and images too: an upload called `/x?id=WEB-42.png` is a URL,
  // not a reference, and rewriting inside an attribute produces neither.
  let out = html.replace(/<a\b[^>]*>[\s\S]*?<\/a>|<img\b[^>]*>/g, (match) => {
    links.push(match);
    return `${links.length - 1}`;
  });

  out = out.replace(/(^|[^\w/-])([A-Z][A-Z0-9]{0,9})-(\d{1,6})\b/g, (match, before: string, key: string, number: string) => {
    if (!known.has(key)) return match;
    return `${before}<a class="md-ref" href="/t/${key}-${number}">${key}-${number}</a>`;
  });

  if (refs.projectHref) {
    out = out.replace(/(^|[^\w#])#([A-Z][A-Z0-9]{0,9})\b/g, (match, before: string, key: string) => {
      const href = known.has(key) ? refs.projectHref!(key) : undefined;
      return href ? `${before}<a class="md-ref" href="${href}">#${key}</a>` : match;
    });
  }

  return out.replace(/(\d+)/g, (_, index: string) => links[Number(index)]);
}

/**
 * A line break inside a paragraph, held as a placeholder until the end.
 *
 * Markdown has two ways of asking for one — a line ending in two spaces, and a
 * line ending in a backslash — and both are noticed before the emphasis rules
 * run. `<br />` written straight out would be text those rules can see, and a
 * paragraph is matched as one string so that emphasis opened on one line can
 * close on the next. The character is private-use, so nothing typed collides.
 */
const BREAK = '';

/**
 * A share of something, drawn as a bar: `{48%}`, `{3/8}`, `{92% ok}`.
 *
 * It exists because of the workaround it replaces. Wanting a bar in a page,
 * somebody wrote `![](https://geps.dev/progress/48)` — and the browser refused
 * the image, because `img-src` is `'self' data: blob:` and will stay that way:
 * a self-hosted work OS that renders a third party's picture of your numbers
 * has told that third party what your numbers are, and shows nothing at all on
 * a train. The bar a page wants is three spans and a width, so it is three
 * spans and a width.
 *
 * Two spellings, because both are what people already write: a percentage, and
 * a fraction — `{3/8}` reads as three of eight and is drawn at 37.5%. A decimal
 * comma is accepted (`{48,5%}`), since the maintainer writes German and so do
 * the documents.
 *
 * **The colour is the author's, never a threshold.** `ok`, `warn` and `danger`
 * are the three words, and nothing is inferred from the number: 20% of a long
 * project is not in trouble, and 90% of a budget is not a success. The one
 * thing read off the number is that past 100% the bar is full — the label still
 * says `{120%}`, because a clamped bar that also clamped its number would be
 * the renderer deciding what is true.
 *
 * Only the *width* reaches an attribute, and only as a number this function
 * computed. The label is put back as the author typed it, and the pattern
 * admits nothing but digits, a separator and the `%` or `/` — so there is no
 * path from the document into the `style`.
 */
const BAR = /\{(\d{1,6}(?:[.,]\d{1,2})?)\s*(%|\/\s*\d{1,6}(?:[.,]\d{1,2})?)(?:\s+(ok|warn|danger))?\}/g;

/** `48,5` and `48.5` are the same number; only the label remembers which. */
const amount = (text: string): number => Number(text.replace(',', '.'));

function bars(html: string): string {
  // Anchors and images whole first, the way `references` does it and for a
  // sharper version of the same reason. A URL may contain braces, and
  // `https://e.com/a{1/2}b` had the bar drawn *inside its own href* — markup in
  // an attribute, which breaks the attribute and the link with it. The cost is
  // that a bar cannot be a link's label, which nobody has ever wanted.
  const links: string[] = [];
  const held = html.replace(/<a\b[^>]*>[\s\S]*?<\/a>|<img\b[^>]*>/g, (match) => {
    links.push(match);
    return `\u{E004}${links.length - 1}\u{E004}`;
  });

  return held.replace(BAR, (match, left: string, unit: string, state: string | undefined) => {
    const top = amount(left);
    const whole = unit === '%' ? 100 : amount(unit.slice(1).trim());
    // A fraction of nothing has no bar to draw. Left as the text it was, which
    // is the rule every unresolved thing in this file follows.
    if (!Number.isFinite(top) || !Number.isFinite(whole) || whole <= 0) return match;
    const share = Math.min(100, Math.max(0, (top / whole) * 100));
    const width = Math.round(share * 10) / 10;
    const label = unit === '%' ? `${left}%` : `${left}/${unit.slice(1).trim()}`;
    return `<span class="md-bar${state ? ` md-bar-${state}` : ''}">`
      + `<span class="md-bar-track"><span class="md-bar-fill" style="width:${width}%"></span></span>`
      + `<span class="md-bar-value">${label}</span></span>`;
  }).replace(/\u{E004}(\d+)\u{E004}/gu, (_, index: string) => links[Number(index)]);
}

function inline(text: string, refs?: MarkdownOptions): string {
  let out = escapeHtml(text);

  // code spans first: their content must not be touched by later rules
  const codes: string[] = [];
  out = out.replace(/`([^`]+)`/g, (_, code: string) => {
    codes.push(code);
    return `${codes.length - 1}`;
  });

  // ...and after that stash, so a line of code that happens to end in a
  // backslash is code rather than a request for a line break.
  out = out.replace(/(?: {2,}|\\)\n/g, BREAK);

  // Wiki links before the link rules and everything after them. `[[…]]` is its
  // own syntax, not a bracketed label looking for a URL, and a title is allowed
  // to contain the characters emphasis is spelled with.
  const pages: string[] = [];
  if (refs?.pageHref) out = pageLinks(out, refs, pages);

  // The optional title is written `&quot;…&quot;` and not `"…"` because the
  // whole string was escaped on the way in, several rules above. Spelled with a
  // literal quote — which is how it read for a long time — the group simply
  // never matched, and a link or an image carrying a title did not lose its
  // title: it stopped being a link at all and came out as the source text.
  out = out.replace(/!\[([^\]]*)\]\(([^)\s]+)(?:\s+&quot;(?:(?!&quot;).)*&quot;)?\)/g, (match, alt: string, src: string) => {
    const url = safeUrl(src);
    return url ? `<img src="${url}" alt="${alt}" loading="lazy" />` : match;
  });

  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)(?:\s+&quot;(?:(?!&quot;).)*&quot;)?\)/g, (match, label: string, href: string) => {
    const url = safeUrl(href);
    if (!url) return match;
    const external = /^https?:/i.test(url);
    return `<a href="${url}"${external ? ' target="_blank" rel="noopener noreferrer"' : ''}>${label}</a>`;
  });

  out = out.replace(/(^|[\s(])(https?:\/\/[^\s<)]+)/g, '$1<a href="$2" target="_blank" rel="noopener noreferrer">$2</a>');
  // After the links, so a URL that happens to hold braces is already an anchor
  // and is left alone; before the emphasis rules, which have nothing to say
  // about braces either way.
  out = bars(out);
  // Bold before italic, and both spelled so the other may sit inside them: the
  // old `[^*]+` could not cross the `*` in `**bold *and* italic**`, so a reader
  // got the asterisks instead of the emphasis. A lone `*` is ordinary text to
  // the bold rule, and the lazy quantifier keeps `**a** and **b**` two pairs
  // rather than one long one.
  out = out.replace(/\*\*((?:[^*]|\*(?!\*))+?)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|\W)__((?:[^_]|_(?!_))+?)__(?=\W|$)/g, '$1<strong>$2</strong>');
  out = out.replace(/(^|\W)_([^_]+)_(?=\W|$)/g, '$1<em>$2</em>');
  out = out.replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, '$1<em>$2</em>');
  out = out.replace(/~~([^~]+)~~/g, '<del>$1</del>');
  // References before the tag rule: `#WEB` is a project when the key is known,
  // and only a tag when it is not.
  if (refs) out = references(out, refs);
  out = out.replace(/(^|\s)(#[a-z0-9][\w-]*)/gi, '$1<span class="md-tag">$2</span>');

  return out
    // Pages first: a label may hold a code span, and that placeholder is only
    // put back on the next line.
    .replace(/(\d+)/g, (_, index: string) => pages[Number(index)])
    .replace(/(\d+)/g, (_, index: string) => `<code>${codes[Number(index)]}</code>`)
    .replace(new RegExp(BREAK, 'g'), '<br />');
}

/**
 * A paragraph's worth of lines, as one run of inline markup.
 *
 * Joined before rendering rather than after, so emphasis that opens on one line
 * and closes on the next still reads as emphasis. The newlines survive into
 * `inline`, which is where the ones the author asked to keep become breaks;
 * what is left is a soft wrap, and a soft wrap is a space.
 */
function inlineBlock(lines: string[], refs?: MarkdownOptions): string {
  return inline(lines.join('\n'), refs).replace(/\n/g, ' ');
}

/* ----------------------------------------------------------------- tables */

const DELIMITER = /^:?-+:?$/;

/**
 * The cells of one table line.
 *
 * The pipes that bracket a row are decoration and come off; a `\|` is a pipe
 * somebody wanted to write and stays one. Splitting happens before any inline
 * rule runs, which is why `` `a | b` `` is two cells and not one — that is what
 * GitHub does with it too, and matching the tool people learned this in is
 * worth more here than being clever.
 */
function row(line: string): string[] {
  let text = line.trim();
  if (text.startsWith('|')) text = text.slice(1);
  if (/(^|[^\\])\|$/.test(text)) text = text.slice(0, -1);

  const cells: string[] = [];
  let cell = '';
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '\\' && text[i + 1] === '|') {
      cell += '|';
      i++;
      continue;
    }
    if (text[i] === '|') {
      cells.push(cell);
      cell = '';
      continue;
    }
    cell += text[i];
  }
  cells.push(cell);
  return cells.map((one) => one.trim());
}

/**
 * The `| --- | :-: |` line under a header, and what each column aligns to.
 *
 * A pipe is required, which is what keeps this from arguing with the other two
 * things a row of dashes can mean: `---` on its own stays a rule, and `---`
 * under a line of prose stays that line's underline.
 */
function alignments(line: string): (string | null)[] | null {
  if (!line.includes('|')) return null;
  const cells = row(line);
  if (!cells.length || !cells.every((cell) => DELIMITER.test(cell))) return null;
  return cells.map((cell) => {
    const left = cell.startsWith(':');
    const right = cell.endsWith(':');
    return left && right ? 'center' : right ? 'right' : left ? 'left' : null;
  });
}

/**
 * Alignment rides on the cell as a style rather than a class, because this
 * markup is read by two stylesheets — the app's and the small inline one a
 * shared page carries — and a style needs neither of them to agree. The values
 * come from a fixed set three lines above, so there is nothing here to inject
 * into.
 */
function table(header: string[], align: (string | null)[], body: string[][], refs?: MarkdownOptions): string {
  const cell = (tag: 'th' | 'td', text: string, at: number): string =>
    `<${tag}${align[at] ? ` style="text-align:${align[at]}"` : ''}>${inline(text, refs)}</${tag}>`;

  return [
    '<table>',
    `<thead><tr>${header.map((text, at) => cell('th', text, at)).join('')}</tr></thead>`,
    ...(body.length
      ? ['<tbody>', ...body.map((cells) => `<tr>${cells.map((text, at) => cell('td', text, at)).join('')}</tr>`), '</tbody>']
      : []),
    '</table>',
  ].join('\n');
}

/* ------------------------------------------------------------------ blocks */

interface ListLevel {
  ordered: boolean;
  indent: number;
  /**
   * Whether this level still has an `<li>` open.
   *
   * A nested list belongs *inside* the item it hangs off, so an item cannot be
   * closed when it is written — only when the next one at that depth arrives,
   * or when the level itself ends. The old renderer closed each `<li>` at once
   * and put the nested `<ul>` between two items, which is markup no browser
   * indents the way the author meant.
   */
  open: boolean;
}

/** How many checkboxes have been drawn, shared across one whole render. */
interface TaskCount {
  n: number;
}

/**
 * What one render has to remember about itself.
 *
 * Three things, and each of them is about the *whole* document rather than the
 * block being written: how many checkboxes have gone by, which heading slugs
 * are already taken, and which pages are open further up an embed. Carried as
 * one object because `blocks` calls itself — for a blockquote, and for an
 * embedded page — and three parameters threaded through two recursions is
 * three chances to thread one of them wrong.
 *
 * `tasks` is the one that is deliberately dropped on the way into a quote; the
 * other two are shared by reference, because a slug taken inside a quote is
 * still taken outside it.
 */
interface Render {
  tasks: TaskCount | null;
  /** The document's slug maker, so two sections called Notes get two ids. */
  slug: (text: string) => string;
  /** The pages open above this one, so an embed cannot re-enter one. */
  embedded: Set<string>;
}

const FENCE = /^(\s*)(`{3,}|~{3,})\s*([^`]*)$/;
const FENCE_END = /^\s*(`{3,}|~{3,})\s*$/;

/**
 * A fenced block. A `mermaid` fence keeps its source as readable code and takes
 * a class: the app swaps in a drawn diagram, and anywhere without that — a
 * shared page carries no script on purpose — the reader still gets the text.
 */
function code(source: string, language: string): string {
  return `<pre${language === 'mermaid' ? ' class="md-mermaid"' : ''}>`
    + `<code${language ? ` class="language-${escapeHtml(language)}"` : ''}>${escapeHtml(source)}</code></pre>`;
}

export function renderMarkdown(source: string, refs?: MarkdownOptions): string {
  return blocks(String(source ?? '').replace(/\r\n?/g, '\n').split('\n'), refs, {
    tasks: { n: 0 }, slug: slugCounter(), embedded: new Set(),
  });
}

/**
 * `![[Onboarding]]` on a line of its own: that page, drawn here.
 *
 * Block-level only, and that is not a limitation waiting to be lifted — half a
 * document inside a sentence is not a thing anybody means, and requiring the
 * line to be nothing else is what keeps this out of the way of the inline
 * rules.
 *
 * A page already open above this one is drawn as a plain link with a word
 * saying why. That is the cycle guard, and it is exact: a counter would cut a
 * legitimately deep chain and look identical to a bug.
 */
function embed(target: string, refs: MarkdownOptions, state: Render): string {
  const found = refs.pageBody!(target);
  if (!found) return '';
  const title = `${escapeHtml(found.title)}`;
  const link = `<a class="md-page" href="${escapeHtml(found.href)}">${title}</a>`;
  if (state.embedded.has(found.id)) {
    const note = refs.embedLoopNote ? `${escapeHtml(refs.embedLoopNote)} ` : '';
    return `<p class="md-embed-loop">${note}${link}</p>`;
  }
  /*
   * The embedded page's headings carry no ids, and that is a correction rather
   * than a simplification. They used to take them from the host's slug maker,
   * so a page embedding another and then writing its own `## Notes` got
   * `h-notes-2` for a section its own outline called `h-notes` — an outline
   * link landing on somebody else's paragraph. An embedded copy is a *view* of
   * a page; the anchors for it live on the page itself.
   */
  const inner = found.format === 'html'
    // An HTML page is put through the allowlist, exactly as it is on its own
    // screen — with no `headingPrefix`, for the reason above, and with the
    // host's `pageHref` so a `[[…]]` inside it still resolves. It cannot
    // contain a further `![[…]]` to recurse into: the sanitiser has no embed
    // syntax, which is why the cycle guard above is enough on its own.
    ? sanitizeHtml(String(found.content ?? ''), { pageHref: refs.pageHref, idPrefix: `u-${found.id}-` })
    : blocks(String(found.content ?? '').replace(/\r\n?/g, '\n').split('\n'), {
      ...refs, headingPrefix: undefined,
    }, {
      // The embedded page's checkboxes are drawn inert: `toggleTask` counts over
      // the *host* page's source and has never seen this text, so a box that
      // ticked here would tick a line somewhere else.
      tasks: null,
      slug: state.slug,
      embedded: new Set([...state.embedded, found.id]),
    });
  return `<figure class="md-embed">\n${inner}\n<figcaption>${link}</figcaption>\n</figure>`;
}

/**
 * One level of block structure.
 *
 * Takes lines rather than a string because it calls itself for the inside of a
 * blockquote. A quote can hold a list, a fence, a table or another quote, and
 * recursing is cheaper — and far less wrong — than teaching every rule below
 * how to see past a `>`.
 *
 * `tasks` is null in there. `toggleTask` rewrites the source by counting
 * checkboxes from the top and its pattern cannot see one behind a `>`, so a
 * checkbox inside a quote is drawn inert and counted by neither. That is what
 * keeps a click and a line of markdown pointing at the same box.
 */
function blocks(lines: string[], refs: MarkdownOptions | undefined, state: Render): string {
  const tasks = state.tasks;
  const html: string[] = [];
  const lists: ListLevel[] = [];
  let paragraph: string[] = [];

  const closeParagraph = () => {
    if (!paragraph.length) return;
    html.push(`<p>${inlineBlock(paragraph, refs)}</p>`);
    paragraph = [];
  };
  const closeLists = (toIndent = -1) => {
    while (lists.length && lists[lists.length - 1].indent > toIndent) {
      const level = lists.pop()!;
      if (level.open) html.push('</li>');
      html.push(level.ordered ? '</ol>' : '</ul>');
    }
  };

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index].replace(/\t/g, '    ');

    const fence = FENCE.exec(line);
    if (fence) {
      closeParagraph();
      closeLists();
      const [, indent, mark, info] = fence;
      const body: string[] = [];
      let end = index + 1;
      for (; end < lines.length; end++) {
        const close = FENCE_END.exec(lines[end].replace(/\t/g, '    '));
        // A fence closes on its own marker, and on one at least as long as the
        // one that opened it — which is how a block of markdown containing
        // ``` is written with ````.
        if (close && close[1][0] === mark[0] && close[1].length >= mark.length) break;
        // The opening indent comes off every line, so a fence inside a list
        // does not arrive with the list's indentation baked into the code.
        body.push(lines[end].startsWith(indent) ? lines[end].slice(indent.length) : lines[end]);
      }
      // Past the closing fence, or past the end when the author never wrote one.
      index = end;
      html.push(code(body.join('\n'), info.trim().split(/\s+/)[0] ?? ''));
      continue;
    }

    if (!line.trim()) {
      closeParagraph();
      closeLists();
      continue;
    }

    // A table announces itself on its second line, so it is recognised from the
    // header with the delimiter in hand. GFM lets one interrupt a paragraph,
    // which is why this sits above everything that would swallow the line.
    const align = index + 1 < lines.length ? alignments(lines[index + 1]) : null;
    const header = align && line.includes('|') ? row(line) : [];
    if (align && header.length === align.length) {
      closeParagraph();
      closeLists();
      const body: string[][] = [];
      let end = index + 2;
      for (; end < lines.length && lines[end].trim() && lines[end].includes('|'); end++) {
        const cells = row(lines[end]);
        // A short row is padded out and a long one loses the extra, so every
        // row has the shape the header promised.
        body.push(Array.from({ length: header.length }, (_, at) => cells[at] ?? ''));
      }
      index = end - 1;
      html.push(table(header, align, body, refs));
      continue;
    }

    // Before the heading rule and everything under it: `![[…]]` alone on a line
    // is a document, not a paragraph starting with an exclamation mark.
    const embedded = refs?.pageBody ? /^\s*!\[\[([^[\]|\n]+)\]\]\s*$/.exec(line) : null;
    if (embedded) {
      const drawn = embed(splitTarget(embedded[1]).target, refs!, state);
      if (drawn) {
        closeParagraph();
        closeLists();
        html.push(drawn);
        continue;
      }
    }

    const heading = /^ {0,3}(#{1,6})\s+(.*?)(?:\s+#+)?\s*$/.exec(line);
    if (heading) {
      closeParagraph();
      closeLists();
      const level = heading[1].length;
      const id = refs?.headingPrefix === undefined ? '' : ` id="${escapeHtml(refs.headingPrefix + state.slug(heading[2]))}"`;
      html.push(`<h${level}${id}>${inline(heading[2], refs)}</h${level}>`);
      continue;
    }

    // A row of `=` or `-` underlining a paragraph is that paragraph's heading.
    // Above the rule below on purpose: `---` under prose is a heading and `---`
    // on its own is a rule, and every markdown people have used agrees.
    const underline = /^ {0,3}(=+|-+)\s*$/.exec(line);
    if (underline && paragraph.length && !lists.length) {
      const level = underline[1][0] === '=' ? 1 : 2;
      const id = refs?.headingPrefix === undefined
        ? ''
        : ` id="${escapeHtml(refs.headingPrefix + state.slug(paragraph.join(' ')))}"`;
      html.push(`<h${level}${id}>${inlineBlock(paragraph, refs)}</h${level}>`);
      paragraph = [];
      continue;
    }

    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) {
      closeParagraph();
      closeLists();
      html.push('<hr />');
      continue;
    }

    if (/^\s*>/.test(line)) {
      closeParagraph();
      closeLists();
      const inner: string[] = [];
      let end = index;
      for (; end < lines.length; end++) {
        const quoted = /^\s*>\s?(.*)$/.exec(lines[end]);
        if (!quoted) break;
        inner.push(quoted[1]);
      }
      index = end - 1;
      html.push(`<blockquote>\n${blocks(inner, refs, { ...state, tasks: null })}\n</blockquote>`);
      continue;
    }

    const item = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/.exec(line);
    if (item) {
      closeParagraph();
      const indent = item[1].length;
      const ordered = /\d/.test(item[2]);
      closeLists(indent);
      let top = lists[lists.length - 1];
      // A bullet where a number was, at the same depth, is a different list and
      // not a stray item in this one.
      if (top && top.indent === indent && top.ordered !== ordered) {
        closeLists(indent - 1);
        top = lists[lists.length - 1];
      }
      if (!top || top.indent < indent) {
        const start = ordered ? parseInt(item[2], 10) : 1;
        html.push(ordered ? `<ol${start !== 1 ? ` start="${start}"` : ''}>` : '<ul>');
        lists.push({ ordered, indent, open: false });
        top = lists[lists.length - 1];
      }
      // The previous item at this depth was left open in case something nested
      // under it. Nothing did, so it closes here.
      if (top.open) html.push('</li>');
      top.open = true;

      const checkbox = /^\[([ xX])\]\s+(.*)$/.exec(item[3]);
      if (checkbox) {
        const checked = checkbox[1].toLowerCase() === 'x';
        // Counted here rather than anywhere else, because this is the one place
        // that has already skipped fenced code — which is exactly the counting
        // `toggleTask` repeats over the source.
        const at = tasks ? tasks.n++ : -1;
        const box = at >= 0
          ? `<input type="checkbox" data-task="${at}"${refs?.interactiveTasks ? '' : ' disabled'}${checked ? ' checked' : ''} />`
          : `<input type="checkbox" disabled${checked ? ' checked' : ''} />`;
        html.push(`<li class="md-task">${box}<span${checked ? ' class="md-done"' : ''}>${inline(checkbox[2], refs)}</span>`);
      } else {
        html.push(`<li>${inline(item[3], refs)}`);
      }
      continue;
    }
    closeLists();

    // Trailing spaces survive: two of them are how a line asks for a break, and
    // trimming both ends here is what used to throw that away.
    paragraph.push(line.replace(/^\s+/, ''));
  }

  closeParagraph();
  closeLists();
  return html.join('\n');
}

/** First meaningful line, for previews and list rows. */
export function excerpt(source: string, max = 140): string {
  const text = String(source ?? '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    // A page link reads as what it is called. Before the markdown-link rule,
    // whose `\[([^\]]*)\]` would otherwise take `[[Onboarding]]` apart into a
    // stray bracket and leave the card saying `[Onboarding]`.
    .replace(/\[\[([^[\]|\n]+)(?:\|([^[\]\n]*))?\]\]/g, (_, title: string, alias: string | undefined) =>
      (alias?.trim() || title.trim()))
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    // A bar reads as its number: `{3/8}` is "3/8". Before the punctuation sweep
    // below, which takes the `-` out of a fraction's braces and leaves them.
    .replace(BAR, (_, left: string, unit: string) =>
      (unit === '%' ? `${left}%` : `${left}/${unit.slice(1).trim()}`))
    .replace(/[#>*_`~|-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/**
 * The preview line for a page, whichever language it is written in.
 *
 * One function because the callers are list rows and cards, and a card that
 * showed `<p class="lead">Willkommen` for one page and `Willkommen` for the
 * next would be telling the reader about the database.
 */
export const pageExcerpt = (content: string | null | undefined, format: string | null | undefined, max = 140): string =>
  excerpt(format === 'html' ? htmlText(String(content ?? '')) : String(content ?? ''), max);

/** A document, and what it is called — one piece of something that was split. */
export interface Section {
  /** The heading it was cut at, or `null` for whatever came before the first. */
  title: string | null;
  content: string;
}

/**
 * One long document, cut into pages at its headings.
 *
 * This exists for the shape an import actually arrives in. Nobody exports a
 * wiki as a folder of small files; they export it as one file with forty
 * chapters in it, and importing that as a single page is importing a
 * scroll — searchable, unlinkable, and impossible to give anybody a link into.
 * Cutting it at `#` gives back the pages the document already had.
 *
 * Fenced code is skipped, for the reason every other counter here skips it: a
 * `# comment` in a shell example is not a chapter, and one of those in the
 * middle of a runbook would cut the runbook in half.
 *
 * The heading line is kept in the piece it opens. The page's title and its
 * first line then say the same thing, which reads as a repeat — and the
 * alternative is worse: an export that loses the heading level structure of
 * everything nested under it and cannot be put back together.
 */
export function splitByHeadings(source: string, level = 1): Section[] {
  const raw = String(source ?? '').split('\n');
  // The same lines with code blanked out, so the cut is decided by `headingAt`
  // over a document where a `# rebuild the index` inside a shell block is not a
  // chapter — while what is *emitted* is still the author's own text.
  const seen = withoutCode(String(source ?? '')).split('\n');
  const out: Section[] = [];
  let current: Section = { title: null, content: '' };
  let buffer: string[] = [];

  const close = (): void => {
    const content = buffer.join('\n').trim();
    if (content) out.push({ ...current, content });
    buffer = [];
  };

  for (let at = 0; at < raw.length; at += 1) {
    const heading = headingAt(seen, at);
    if (heading && heading.level === level) {
      close();
      current = { title: heading.text || null, content: '' };
    }
    buffer.push(raw[at]);
    // An underlined heading is two lines and both belong to the section it
    // opens; taking only the first would leave a row of `=` on the one before.
    if (heading && heading.spans === 2) {
      buffer.push(raw[at + 1] ?? '');
      at += 1;
    }
  }
  close();
  return out;
}
